<template>
  <Layout>
    <template v-slot:top_left>
      <div class="text-default">
        <q-avatar>
          <q-icon
            size="md"
            name="contact_support"
          />
        </q-avatar>
        {{ i18n.page_type_title }}
      </div>
    </template>

    <template v-slot:header>
      <Link
        :href="workspace_action_path"
      >
        <q-avatar
          rounded
          color="transparent"
          size="18px"
        >
          <Jdenticon
            :text="shared.workspace.id"
            :size="18"
          />
        </q-avatar>
        &nbsp;
        <span class="text-subtitle2 text-bold items-center text-default">
          {{ shared.workspace.name }}
        </span>
      </Link>
    </template>

    <template v-slot:default>
      <q-card flat>
        <div class="centered-container-medium fill-height q-px-xl q-pt-xl">
          <div class="row q-mt-lg items-center justify-between q-mb-lg">
            <div class="col-12">
              <div class="doc-h1">
                {{ i18n.page_title }}
              </div>
              <p>
                {{ i18n.page_description }}
              </p>

              <div class="doc-h2">
                {{ i18n.section_1_title }}
              </div>
              <div class="doc-h3">
                1) {{ i18n.section_1_1_title }}
              </div>
              <p>
                {{ i18n.section_1_1_text }}
              </p>

              <q-btn
                class="q-mt-md"
                color="primary"
                unelevated
                :label="template_link.label"
                icon="get_app"
                no-caps
                :href="template_link.url"
              />

              <div class="doc-h3">
                2) {{ i18n.section_1_2_title }}
              </div>
              <p>{{ i18n.section_1_2_text }}</p>

              <div class="docH2">
                {{ i18n.section_2_title }}
              </div>
              <div class="docH3">
                {{ i18n.section_2_1_title }}
              </div>
              <p v-html="i18n.section_2_1_text" />

              <ol>
                <li><strong>Timestamp</strong></li>
                <li><strong>Operation Type</strong></li>
                <li><strong>Operation Sub Type *</strong></li>
                <li><strong>Base Amount</strong></li>
                <li><strong>Base Currency</strong></li>
                <li><strong>Fiat Amount *</strong></li>
                <li><strong>Quote Amount *</strong></li>
                <li><strong>Quote Currency *</strong></li>
                <li><strong>Fee Amount *</strong></li>
                <li><strong>Fee Currency *</strong></li>
                <li><strong>Fee Fiat Amount *</strong></li>
                <li><strong>From *</strong></li>
                <li><strong>To *</strong></li>
                <li><strong>Transaction Id *</strong></li>
                <li><strong>Notes *</strong></li>
              </ol>

              <p>{{ i18n.optional_field_footnote }}</p>

              <q-banner
                class="bg-pastel-green q-pa-md"
                dense
              >
                <template v-slot:avatar>
                  <q-icon
                    name="las la-info-circle"
                    class="message-box-text"
                    size="sm"
                  />
                </template>
                <div
                  class=""
                  v-html="i18n.csv_amount_note"
                />
              </q-banner>

              <div class="doc-h3">
                {{ i18n.section_2_2_title }}
              </div>

              <q-expansion-item label="Timestamp">
                <div class="q-px-lg q-py-md">
                  <p v-html="i18n.timestamp_description" />

                  <ul class="list-none list-inside pl-5">
                    <li>
                      <p v-html="i18n.timestamp_format_1" />
                    </li>
                    <li class="q-pt-sm">
                      <p v-html="i18n.timestamp_format_2" />
                    </li>
                  </ul>

                  <p>
                    {{ i18n.timestamp_link_label }}
                    <a
                      href="https://www.w3.org/TR/NOTE-datetime"
                      target="_blank"
                    >https://www.w3.org/TR/NOTE-datetime</a>
                  </p>
                </div>
              </q-expansion-item>

              <q-expansion-item label="Operation Type">
                <div class="q-px-lg q-py-md">
                  <p v-html="i18n.operation_type_description" />

                  <ul class="list-disc list-inside pl-5">
                    <li><strong>deposit</strong></li>
                    <li><strong>withdrawal</strong></li>
                    <li><strong>trade</strong></li>
                  </ul>
                  <br>

                  <p>
                    {{ i18n.operation_type_subtype_note }}
                  </p>

                  <p><strong>Deposit / Withdrawal</strong></p>
                  <p v-html="i18n.operation_type_deposit_withdrawal_desc" />

                  <p v-html="i18n.operation_type_deposit_withdrawal_note" />

                  <p>
                    {{ i18n.operation_type_fee_note }}
                  </p>
                  <p v-html="i18n.operation_type_only_commission_note" />

                  <p><strong>Trade</strong></p>

                  <p>{{ i18n.operation_type_trade_desc }}</p>
                </div>
              </q-expansion-item>

              <q-expansion-item label="Operation Sub Type">
                <div class="q-px-lg q-py-md">
                  <p>
                    <strong>{{ i18n.operation_subtype_desc }}</strong>
                  </p>

                  <q-item
                    v-for="kind in transaction_kinds"
                    :key="kind.id"
                  >
                    <q-item-section>
                      <q-item-label>
                        <b>{{ kind.name }}</b> ({{ kind.label }})
                      </q-item-label>
                      <q-item-label caption>
                        {{ kind.description }}
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                </div>
              </q-expansion-item>

              <q-expansion-item label="Base Amount">
                <div class="q-px-lg q-py-md">
                  <p><b>“Base Amount”</b> {{ i18n.base_amount_desc }}</p>
                </div>
              </q-expansion-item>

              <q-expansion-item label="Base Currency">
                <div class="q-px-lg q-py-md">
                  <p><b>“Base Currency”</b> {{ i18n.basecurrency_desc }}</p>
                </div>
              </q-expansion-item>

              <q-expansion-item label="Fiat Amount">
                <div class="q-px-lg q-py-md">
                  <p><b>“Fiat Amount”</b> {{ i18n.fiat_amount_desc }}</p>
                </div>
              </q-expansion-item>

              <q-expansion-item label="Quote Amount">
                <div class="q-px-lg q-py-md">
                  <p><b>“Quote Amount”</b> {{ i18n.quote_amount_desc }}</p>
                </div>
              </q-expansion-item>

              <q-expansion-item label="Quote Currency">
                <div class="q-px-lg q-py-md">
                  <p><b>“Quote Currency”</b> {{ i18n.quote_currency_desc }}</p>
                </div>
              </q-expansion-item>

              <q-expansion-item label="Fee Amount">
                <div class="q-px-lg q-py-md">
                  <p><b>“Fee Amount”</b> {{ i18n.fee_amount_desc }}</p>
                </div>
              </q-expansion-item>

              <q-expansion-item label="Fee Currency">
                <div class="q-px-lg q-py-md">
                  <p><b>“Fee Currency”</b> {{ i18n.fee_currency_desc }}</p>
                </div>
              </q-expansion-item>

              <q-expansion-item label="Fee Fiat Amount">
                <div class="q-px-lg q-py-md">
                  <p><b>“Fee Fiat Amount”</b> {{ i18n.fee_fiat_amount_desc }}</p>
                </div>
              </q-expansion-item>

              <q-expansion-item label="From">
                <div class="q-px-lg q-py-md">
                  <p><b>“From”</b> {{ i18n.from_desc }}</p>
                </div>
              </q-expansion-item>

              <q-expansion-item label="To">
                <div class="q-px-lg q-py-md">
                  <p><b>“To”</b> {{ i18n.to_desc }}</p>
                </div>
              </q-expansion-item>

              <q-expansion-item label="Transaction Id">
                <div class="q-px-lg q-py-md">
                  <p><b>“Transaction Id”</b> {{ i18n.transaction_id_desc }}</p>
                </div>
              </q-expansion-item>

              <q-expansion-item label="Notes">
                <div class="q-px-lg q-py-md">
                  <p><b>“Notes”</b> {{ i18n.notes_desc }}</p>
                </div>
              </q-expansion-item>
            </div>
          </div>
        </div>

        <q-card-actions align="right" />
      </q-card>
    </template>
  </Layout>
</template>
<script>
import Layout from '@/layouts/wide.vue';
import Jdenticon from '@/shared/jdenticon.vue';
import { Link } from '@inertiajs/vue3';

export default {
  components: {
    Jdenticon,
    Layout,
    Link,
  },
  props: {
    shared: {
      type: Object,
      required: true,
    },
    i18n: {
      type: Object,
      required: true,
    },
    workspace_action_path: {
      type: String,
      required: true,
    },
    transaction_kinds: {
      type: Array,
      required: true,
    },
    template_link: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {};
  },
  computed: {
  },
  watch: {
  },
  methods: {
  },
};
</script>
<style scoped>
.doc-h1 {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.doc-h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 60px;
  margin-bottom: 0.5rem;
}

.doc-h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.q-item {
  user-select: text;
}

p {
  margin-bottom: 1rem;
}
.q-expansion-item {
  margin-bottom: 1rem;
}
.q-expansion-item--expanded {
  box-shadow: 0 0 0 1px var(--q-accent);
  border-radius: 8px;
}
</style>
